import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LoginForm } from './LoginForm/LoginForm';
import version from '../../service/version';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { MessageDialog } from '../MessageDialog/MessageDialog';
import { ProgressIndicatorOverlay } from '../Progress/ProgressIndicatorOverlay/ProgressIndicatorOverlay';
import { AppSettings } from './AppSettings/AppSettings';
import { Typography } from '../material-ui/Typography';
import { login, setSettings, SHOW_ERROR } from '../../actions/login';
import { showModal, TYPES } from '../../actions/modal';
import { useStyles } from './useStyles';

import './LoginPage.scss';
import { PrevStepButton } from './PrevStepButton/PrevStepButton';
import { LoginStep, useStep } from './hooks/useStep';
import Logo from '../Logo/Logo';

export const LoginPage = memo(() => {
    const { error = '', backendUrl, ssl, backupInterval } = useSelector((state) => state.login || {});
    const dispatch = useDispatch();
    const classes = useStyles();
    const { step, showStep } = useStep();
    const { t: translate } = useTranslation();
    const company = process.env.COMPANY;
    const isCustomCompany = process.env.IS_CUSTOM_COMPANY;

    const handleLogin = useCallback((username, password, eng, confirmationCode, onError) => {
        dispatch({
            type: SHOW_ERROR,
            error: '',
        });
        dispatch(login({ login: username, password, eng, confirmationCode, onError }));
    }, []);

    const handleChangeSettings = useCallback((backendUrl, ssl, backupInterval) => {
        dispatch(setSettings(backendUrl, ssl, backupInterval));
    }, []);

    const handleShowRestoreModal = useCallback((props) => {
        dispatch(showModal(TYPES.RESTORE, props));
    }, []);

    const appName = translate('program', { company });

    return (
        <div className="login-page">
            <ProgressIndicatorOverlay />
            {!isCustomCompany && <LanguageSelector variant="outlined" />}
            <AppSettings
                onChangeSettings={handleChangeSettings}
                onRestore={handleShowRestoreModal}
                backendUrl={backendUrl}
                ssl={ssl}
                backupInterval={backupInterval}
            />
            {step !== LoginStep.LOGIN && <PrevStepButton onClick={showStep} />}
            <div className={classes.inner}>
                <div className={classes.form} style={{ background: 'rgba(26, 56, 87, 0.3)' }}>
                    <div className={classes.companyInfo} style={{ background: 'rgba(40, 80, 120, 1)' }}>
                        <Logo isCustomCompany={isCustomCompany} width={120} height={120} />
                        <Typography style={{ paddingTop: 8 }} className={classes.title} variant="h1">
                            {isCustomCompany ? 'Electronic Flight Bag' : appName.charAt(0).toUpperCase() + appName.slice(1)}
                        </Typography>
                    </div>
                    <LoginForm error={error} onLogin={handleLogin} step={step} showStep={showStep} theme="DARK" />
                </div>
            </div>
            <footer className={classes.footer}>
                {isCustomCompany ? (
                    ''
                ) : (
                    <Typography className={classes.copyright}>
                        <a title={translate('companySite')} className={classes.link} target="_blank" rel="noopener noreferrer">
                            &copy;
                            {`${company} ${version.copyright}`}
                        </a>
                    </Typography>
                )}
                <Typography className={classes.version}>{version.toString()}</Typography>
            </footer>
            <MessageDialog />
        </div>
    );
});
